import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { addStore, fetchStores, updateStore } from "../../../actions/storeAction";
import {
  getStoreBanner,
  getStoreLogo,
  getStoreQrisPayload,
  normalizeStoreSetting,
  resolveAssetUrl,
} from "../../../utils/helpers/storeSetting";
import FormStore from "./FormStore";
import BillingPanel from "./BillingPanel";
import "./StorePage.css";

const initialState = {
  id: "",
  name: "",
  legal_name: "",
  tagline: "",
  email: "",
  phone: "",
  address: "",
  website: "",
  instagram: "",
  facebook: "",
  tiktok: "",
  description: "",
  invoice_footer: "",
  primary_color: "#0B2D32",
  logo: "",
  logo_url: "",
  logo_path: "",
  logo_file: null,
  banner: "",
  banner_url: "",
  banner_path: "",
  banner_file: null,
  qris_image: "",
  qris_image_url: "",
  qris_image_path: "",
  qris_image_file: null,
  qris_payload: "",
  is_active: true,
  is_multi_outlet: false,
};

const StorePlaceholder = ({ label, minHeight = 96 }) => (
  <div
    className="bg-light border rounded-4 d-flex flex-column align-items-center justify-content-center text-center text-secondary p-3"
    style={{ minHeight }}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="mb-2">
      <path d="M15 8h.01" />
      <path d="M3 6a3 3 0 0 1 3 -3h12a3 3 0 0 1 3 3v12a3 3 0 0 1 -3 3h-12a3 3 0 0 1 -3 -3z" />
      <path d="M3 16l5 -5c.928 -.893 2.072 -.893 3 0l5 5" />
      <path d="M14 14l1 -1c.928 -.893 2.072 -.893 3 0l3 3" />
    </svg>
    <span className="fw-semibold">{label}</span>
  </div>
);

const MediaPreview = ({ title, image, placeholder, type = "square", status = "" }) => (
  <div className="card border shadow-sm h-100">
    <div className="card-body">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h4 className="card-title mb-0">{title}</h4>
        {status ? <span className="badge bg-success-lt">{status}</span> : null}
      </div>
      {image ? (
        <div
          className="rounded-4 border bg-light"
          style={{
            minHeight: type === "banner" ? "150px" : "150px",
            backgroundImage: `url(${image})`,
            backgroundSize: type === "banner" ? "cover" : "contain",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
          }}></div>
      ) : (
        <StorePlaceholder label={placeholder} minHeight={type === "banner" ? 150 : 150} />
      )}
    </div>
  </div>
);

const DetailItem = ({ label, value }) => (
  <div className="col-md-6 col-xl-4">
    <div className="border rounded-3 p-3 h-100">
      <div className="text-secondary small mb-1">{label}</div>
      <div className="fw-semibold text-truncate">{value || "-"}</div>
    </div>
  </div>
);

const StorePage = (props) => {
  const { stores, setting, isLoading, error, fetchStores, addStore, updateStore } = props;

  const [formState, setFormState] = useState(initialState);
  const [isEdit, setIsEdit] = useState(false);

  const activeStore = useMemo(
    () => setting || stores.find((store) => store.is_active) || stores[0] || null,
    [setting, stores],
  );
  const normalizedStore = normalizeStoreSetting(activeStore || initialState);
  const logoImage = getStoreLogo(activeStore || {}, "");
  const bannerImage = getStoreBanner(activeStore || {}, "");
  const qrisImage = resolveAssetUrl(
    activeStore?.qris_image_url || activeStore?.qris_image_path || activeStore?.qris_image,
    "",
  );
  const hasDynamicQris = Boolean(getStoreQrisPayload(activeStore || {}, ""));

  const refreshStores = () => fetchStores(1, 1, "");

  const closeModal = () => {
    setFormState(initialState);
    setIsEdit(false);
  };

  const openConfigure = () => {
    const store = activeStore || initialState;

    setIsEdit(Boolean(store?.id));
    setFormState({
      ...initialState,
      ...store,
      logo_url: store.logo_url || store.logo_path || store.logo || "",
      banner_url: store.banner_url || store.banner_path || store.banner || "",
      qris_image_url: store.qris_image_url || store.qris_image_path || store.qris_image || "",
      qris_payload: store.qris_payload || "",
      logo_file: null,
      banner_file: null,
      qris_image_file: null,
      primary_color: store.primary_color || initialState.primary_color,
      is_active: true,
      is_multi_outlet: Boolean(store?.is_multi_outlet),
    });
  };

  const handleAdd = async () => {
    const success = await addStore({ ...formState, is_active: true });

    if (success) {
      closeModal();
      refreshStores();
    }

    return success;
  };

  const handleUpdate = async () => {
    const success = await updateStore(formState.id, { ...formState, is_active: true });

    if (success) {
      closeModal();
      refreshStores();
    }

    return success;
  };

  useEffect(() => {
    refreshStores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="page-wrapper">
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <h1 className="page-title fs-1 text-capitalize">Konfigurasi Toko</h1>
              <div className="page-pretitle">master toko</div>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <button
                type="button"
                className="btn btn-primary btn-5"
                data-bs-toggle="modal"
                data-bs-target="#modal-store-form"
                onClick={openConfigure}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="icon icon-2">
                  <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" />
                  <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z" />
                  <path d="M16 5l3 3" />
                </svg>
                Edit konfigurasi
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          {error ? <div className="alert alert-danger">{error}</div> : null}

          <div className="row row-deck row-cards">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  {isLoading && !activeStore ? (
                    <div className="row align-items-center">
                      <div className="col-auto">
                        <div className="avatar avatar-xl rounded placeholder"></div>
                      </div>
                      <div className="col">
                        <div className="placeholder placeholder-lg w-25 mb-2"></div>
                        <div className="placeholder w-50"></div>
                      </div>
                    </div>
                  ) : (
                    <div className="row align-items-center g-3">
                      <div className="col-auto">
                        {logoImage ? (
                          <span
                            className="avatar avatar-xl bg-light border rounded"
                            style={{
                              backgroundImage: `url(${logoImage})`,
                              backgroundSize: "contain",
                              backgroundRepeat: "no-repeat",
                              backgroundPosition: "center",
                            }}></span>
                        ) : (
                          <div
                            className="avatar avatar-xl bg-light border rounded d-flex align-items-center justify-content-center text-center text-secondary"
                            style={{ fontSize: "0.65rem", lineHeight: 1.1 }}>
                            Tambahkan logo
                          </div>
                        )}
                      </div>
                      <div className="col">
                        <h3 className="mb-1">{normalizedStore.name}</h3>
                        <div className="text-secondary">
                          {normalizedStore.tagline ||
                            "Konfigurasi identitas toko agar sistem bisa dipakai banyak UMKM."}
                        </div>
                        <div className="mt-2 d-flex flex-wrap gap-2">
                          {normalizedStore.email ? (
                            <span className="badge bg-primary-lt">{normalizedStore.email}</span>
                          ) : null}
                          {normalizedStore.phone ? (
                            <span className="badge bg-success-lt">{normalizedStore.phone}</span>
                          ) : null}
                          {normalizedStore.website ? (
                            <span className="badge bg-warning-lt">{normalizedStore.website}</span>
                          ) : null}
                          <span className="store-color-badge">
                            <i style={{ background: normalizedStore.primary_color }} /> Warna utama{" "}
                            {normalizedStore.primary_color}
                          </span>
                        </div>
                      </div>
                      <div className="col-auto d-flex flex-column gap-2 align-items-end">
                        <span className="badge bg-success-lt">1 data aktif</span>
                        <span
                          className={`badge ${normalizedStore.is_multi_outlet ? "bg-primary-lt" : "bg-secondary-lt"}`}>
                          {normalizedStore.is_multi_outlet ? "Multi outlet aktif" : "Single outlet"}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <MediaPreview title="Logo Sistem" image={logoImage} placeholder="Tambahkan logo" />
            </div>
            <div className="col-md-4">
              <MediaPreview title="Banner Toko" image={bannerImage} placeholder="Tambahkan banner" type="banner" />
            </div>
            <div className="col-md-4">
              <MediaPreview
                title="QRIS Pembayaran"
                image={qrisImage}
                placeholder="Tambahkan QRIS"
                status={hasDynamicQris ? "Dinamis siap" : ""}
              />
            </div>

            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <div>
                    <h3 className="card-title mb-0">Identitas Toko</h3>
                    <p className="text-secondary m-0">
                      Data ini dipakai untuk sidebar, login, POS, invoice, dan laporan.
                    </p>
                  </div>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <DetailItem label="Nama Toko" value={normalizedStore.name} />
                    <DetailItem label="Nama Legal" value={normalizedStore.legal_name} />
                    <DetailItem label="Tagline" value={normalizedStore.tagline} />
                    <DetailItem label="Warna Utama Aplikasi" value={normalizedStore.primary_color} />
                    <DetailItem
                      label="QRIS POS"
                      value={hasDynamicQris ? "Dinamis · nominal otomatis" : "Belum dikonfigurasi"}
                    />
                    <DetailItem label="Email" value={normalizedStore.email} />
                    <DetailItem label="Telepon / WhatsApp" value={normalizedStore.phone} />
                    <DetailItem label="Website" value={normalizedStore.website} />
                    <DetailItem
                      label="Mode Outlet"
                      value={normalizedStore.is_multi_outlet ? "Multi outlet / multi cabang" : "Single outlet"}
                    />
                    <DetailItem label="Instagram" value={normalizedStore.instagram} />
                    <DetailItem label="Facebook" value={normalizedStore.facebook} />
                    <DetailItem label="TikTok" value={normalizedStore.tiktok} />
                    <div className="col-12">
                      <div className="border rounded-3 p-3 h-100">
                        <div className="text-secondary small mb-1">Footer Invoice dan Struk</div>
                        <div className="fw-semibold">{normalizedStore.invoice_footer || "-"}</div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="border rounded-3 p-3 h-100">
                        <div className="text-secondary small mb-1">Alamat</div>
                        <div className="fw-semibold">{normalizedStore.address || "-"}</div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="border rounded-3 p-3 h-100">
                        <div className="text-secondary small mb-1">Deskripsi</div>
                        <div className="fw-semibold">{normalizedStore.description || "-"}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card-footer d-flex justify-content-between align-items-center">
                  <span className="text-secondary">
                    {normalizedStore.is_multi_outlet
                      ? "Mode multi outlet aktif: fitur cabang dapat dikembangkan dari konfigurasi toko ini."
                      : "Mode single outlet: sistem memakai satu konfigurasi toko aktif."}
                  </span>
                  <button
                    type="button"
                    className="btn bg-secondary-lt"
                    data-bs-toggle="modal"
                    data-bs-target="#modal-store-form"
                    onClick={openConfigure}>
                    Edit konfigurasi
                  </button>
                </div>
              </div>
            </div>

            <div className="col-12 mt-4">
              <BillingPanel />
            </div>
          </div>
        </div>
      </div>

      <FormStore
        isEdit={isEdit}
        closeModal={closeModal}
        formState={formState}
        setFormState={setFormState}
        isLoading={isLoading}
        handleAdd={handleAdd}
        handleUpdate={handleUpdate}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  stores: state?.stores?.datas?.data || [],
  setting: state?.stores?.setting,
  isLoading: state?.stores?.loading,
  error: state?.stores?.error,
});

export default connect(mapStateToProps, { fetchStores, addStore, updateStore })(StorePage);
